/**
 * Visibility rules for the agent's in-call panel.
 *
 * The panel used to force itself open every time `isActive` or
 * `awaitingDisposition` changed. Those flags toggle repeatedly while a single
 * call runs, so an agent who minimized the panel saw it spring back open and had
 * no way to clear it but reloading the page. These helpers keep the "should the
 * panel open itself?" decision in one testable place.
 */

/**
 * True when the panel may re-expand on its own.
 *
 * `autoExpandedFor` is the call the panel last auto-expanded for. `undefined`
 * means no call has been shown yet and is deliberately distinct from `null` (a
 * call that carries no lead id): treating them as the same value would either
 * suppress the very first open, or re-expand on every render for id-less calls.
 */
export function shouldAutoExpandCallPanel(args: {
  autoExpandedFor: string | null | undefined;
  callKey: string | null;
}): boolean {
  return args.autoExpandedFor !== args.callKey;
}

/**
 * True when the minimized pill should advertise a pending disposition rather
 * than posing as a live call. Only reachable now that a minimize survives
 * wrap-up.
 */
export function panelNeedsDisposition(args: {
  awaitingDisposition: boolean;
  isActive: boolean;
}): boolean {
  return args.awaitingDisposition && !args.isActive;
}
